// One shape for a run's steps, whichever runner produced them.
//
// The three runners record the same facts under different names. Nothing said so
// out loud, so every screen that rendered steps quietly picked one runner's
// spelling and showed the others as blanks — an exe case drew a row of green
// ticks down a test that had plainly failed, with no detail line and no
// screenshot button, because the reader was looking for `status`, `reason` and
// `screenshot_path` and the exe agent writes `success`, `detail` and `has_shot`.
//
//   field         exe          extension        cloud
//   ----------------------------------------------------------------
//   verdict       success      status           status
//   what happened detail       reason           reason
//   element       selector     selector_used    target
//   screenshot    has_shot     has_screenshot   screenshot_path
//   position      (implicit)   index            step_index
//
// Add a runner here, not at the call sites.

/** PASS | FAIL | INCONCLUSIVE | ERROR | "" — "" means the runner didn't say. */
function verdict(step) {
  const declared = String(step.status || "").trim().toUpperCase();
  if (declared) {
    // Some surfaces use the "-ED" forms; treat them as the same verdict.
    return { PASSED: "PASS", FAILED: "FAIL", ERRORED: "ERROR" }[declared] || declared;
  }
  // The exe agent reports a boolean per step instead of a status. `undefined`
  // is NOT a pass — it means the step never reported, and calling it green is
  // how a failed run came to show seven green ticks.
  if (step.success === true) return "PASS";
  if (step.success === false) return "FAIL";
  return "";
}

/**
 * @param {Array}  steps        raw steps off the run
 * @param {Array}  shotIndexes  step indexes with a screenshot on disk, from the
 *                              run payload — the only reliable source, since no
 *                              runner writes a screenshot path back onto a step
 */
export function normalizeSteps(steps, shotIndexes = []) {
  const onDisk = new Set((shotIndexes || []).map(Number));
  return (steps || []).map((s, i) => {
    const index = Number(s.index ?? s.step_index ?? i);
    return {
      index,
      action: s.action || "",
      status: verdict(s),
      // The full account of what happened. Never truncated here — the failure
      // narrative is the single most useful thing on the screen.
      text: String(s.reason || s.detail || "").trim(),
      target: s.target || s.selector || s.selector_used || "",
      inputValue: s.input_value ?? s.value ?? null,
      description: s.user_action_description || s.description || "",
      hasShot: !!s.screenshot_path || !!s.has_shot || !!s.has_screenshot
        || onDisk.has(index),
    };
  });
}

/** The steps that went wrong, in run order. */
export function failedSteps(steps) {
  return steps.filter((s) => ["FAIL", "ERROR"].includes(s.status));
}
